<template>
  <div class="score-query card">
    <h2 class="title">成绩查询</h2>
    <div class="filters">
      <input class="input" v-model="filters.exam_title" placeholder="考试名称" />
      <input class="input" type="date" v-model="filters.start" />
      <input class="input" type="date" v-model="filters.end" />
      <button class="btn" @click="fetchScores">查询</button>
    </div>
    <div v-if="loading" class="hint">加载中…</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <!-- 已有成绩的考试 -->
      <div v-if="scores.length">
        <h3 class="section-title">我的成绩</h3>
        <table class="score-table">
          <thead>
            <tr>
              <th>考试名称</th>
              <th>考试时间</th>
              <th>我的分数</th>
              <th>各科成绩</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in paginatedScores" :key="s.id">
              <td>{{ s.exam_title }}</td>
              <td>{{ s.exam_time_text || formatExamTime(s.exam_start, s.exam_end) }}</td>
              <td><strong class="score">{{ s.score ?? '-' }}</strong></td>
              <td>
                <div v-if="s.subject_scores && Object.keys(s.subject_scores).length > 0" class="subject-scores">
                  <span v-for="(score, subject) in s.subject_scores" :key="subject" class="subject-item">
                    {{ subject }}: {{ score }}
                  </span>
                </div>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- 分页 -->
        <div class="pagination">
          <span class="pagination-total">共 {{ scores.length }} 条</span>
          <select v-model="scorePageSize" @change="scorePage = 1" class="page-size-select">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
          </select>
          <button class="btn-page" :disabled="scorePage <= 1" @click="scorePage--">‹</button>
          <span class="page-current">{{ scorePage }}</span>
          <button class="btn-page" :disabled="scorePage >= scoreTotalPages" @click="scorePage++">›</button>
        </div>
      </div>

      <!-- 已报名但尚未有成绩的考试 -->
      <div v-if="pendingExams.length" class="pending-section">
        <h3 class="section-title">待出成绩</h3>
        <table class="score-table pending-table">
          <thead>
            <tr>
              <th>考试名称</th>
              <th>考试时间</th>
              <th>报名状态</th>
              <th>成绩状态</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in pendingExams" :key="e.exam_id">
              <td>{{ e.exam_title }}</td>
              <td>{{ e.exam_time }}</td>
              <td>
                <span class="status-tag status-approved">{{ e.audit_status === 'approved' ? '已通过' : '待审核' }}</span>
              </td>
              <td>
                <span class="status-tag status-pending">{{ e.score_status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!scores.length && !pendingExams.length" class="hint">暂无成绩记录</div>
    </div>
  </div>
</template>

<script>
import { listMyScores, listMyApplications } from "../api/exams"

export default {
  name: 'ScoreQuery',
  data() {
    return {
      scores: [],
      pendingExams: [],
      loading: false,
      error: '',
      scorePage: 1,
      scorePageSize: 10,
      filters: {
        exam_title: '',
        start: '',
        end: ''
      }
    }
  },
  computed: {
    scoreTotalPages() {
      return Math.ceil(this.scores.length / this.scorePageSize) || 1
    },
    paginatedScores() {
      const start = (this.scorePage - 1) * this.scorePageSize
      return this.scores.slice(start, start + this.scorePageSize)
    }
  },
  methods: {
    async fetchScores() {
      this.error = ''
      this.loading = true
      try {
        const params = {}
        if (this.filters.exam_title) params.exam_title = this.filters.exam_title
        if (this.filters.start) params.exam_start_time__gte = this.filters.start
        if (this.filters.end) params.exam_end_time__lte = this.filters.end
        
        // 获取已有成绩
        const raw = await listMyScores(params)
        console.log('成绩API返回数据:', raw) // 调试日志
        const mapItem = (x) => {
          // 优先使用 ticket_time_text，其次使用 start_time/end_time
          const examTimeText = x?.exam?.ticket_time_text || null
          const examStart = x?.exam?.start_time || null
          const examEnd = x?.exam?.end_time || null
          return {
            id: x.id,
            exam_id: x?.exam?.id,
            exam_title: x?.exam?.title || '-',
            exam_time_text: examTimeText,
            exam_start: examStart,
            exam_end: examEnd,
            total_score: x?.exam?.total_score ?? null,
            passing_score: x?.exam?.passing_score ?? null,
            score: x?.score ?? null,
            subject_scores: x?.subject_scores || {},  // 各科成绩
            ticket_subjects: x?.exam?.ticket_subjects || null,  // 科目配置（含及格分数）
            is_passed: !!x?.is_passed,
            time: x?.submit_time || x?.create_time || null,
          }
        }
        this.scores = Array.isArray(raw) ? raw.map(mapItem) : []
        
        // 获取已报名的考试，找出已结束但尚未有成绩的
        const apps = await listMyApplications()
        const scoreExamIds = new Set(this.scores.map(s => s.exam_id))
        const now = new Date()
        
        this.pendingExams = (Array.isArray(apps) ? apps : [])
          .filter(app => {
            // 只显示审核通过且已缴费的报名
            if (app.audit_status !== 'approved' || app.payment_status !== 'paid') return false
            // 排除已有成绩的考试
            if (scoreExamIds.has(app.exam_id)) return false
            return true
          })
          .map(app => {
            // 获取考试时间，优先使用 ticket_time_text，其次从 exam 对象获取 start_time/end_time
            const examTimeText = app.exam?.ticket_time_text || null
            const examStartTime = app.exam?.start_time || null
            const examEndTime = app.exam?.end_time || null
            
            // 调试日志：输出原始数据
            console.log('报名数据:', {
              exam_id: app.exam_id,
              exam_title: app.exam_title || app.exam?.title,
              ticket_time_text: examTimeText,
              start_time: examStartTime,
              end_time: examEndTime,
              ticket_subjects: app.exam?.ticket_subjects
            })
            
            // 判断考试是否结束
            const now = new Date()
            let isEnded = false
            
            // 1. 尝试解析 ticket_time_text（格式如 "2025年12月20日 下午 12:45-12:47"）
            if (examTimeText) {
              try {
                // 提取日期部分：2025年12月20日
                const dateMatch = examTimeText.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
                // 提取结束时间：12:47（支持多种格式）
                const timeMatch = examTimeText.match(/(\d{1,2}):(\d{2})\s*[-~至]\s*(\d{1,2}):(\d{2})/)
                // 检查是否是下午时间
                const isPM = examTimeText.includes('下午')
                console.log('解析ticket_time_text:', { examTimeText, dateMatch, timeMatch, isPM })
                if (dateMatch && timeMatch) {
                  const year = parseInt(dateMatch[1])
                  const month = parseInt(dateMatch[2]) - 1
                  const day = parseInt(dateMatch[3])
                  let endHour = parseInt(timeMatch[3])
                  const endMin = parseInt(timeMatch[4])
                  // 如果是下午且小时数小于12，需要加12（12点不需要加）
                  if (isPM && endHour < 12) {
                    endHour += 12
                  }
                  const endDateTime = new Date(year, month, day, endHour, endMin)
                  console.log('解析结果:', { endDateTime: endDateTime.toString(), now: now.toString(), isEnded: endDateTime < now })
                  if (!isNaN(endDateTime.getTime()) && endDateTime < now) {
                    isEnded = true
                  }
                }
              } catch (e) {
                console.warn('解析ticket_time_text失败', e)
              }
            }
            
            // 2. 尝试从 ticket_subjects 解析最后一个科目的结束时间
            if (!isEnded && app.exam?.ticket_subjects) {
              try {
                const subjectsConfig = typeof app.exam.ticket_subjects === 'string' 
                  ? JSON.parse(app.exam.ticket_subjects) 
                  : app.exam.ticket_subjects
                console.log('解析ticket_subjects:', subjectsConfig)
                
                // 收集所有科目（从byPosition和common中）
                let allSubjects = []
                if (subjectsConfig.byPosition && Array.isArray(subjectsConfig.byPosition)) {
                  subjectsConfig.byPosition.forEach(posSubjects => {
                    if (Array.isArray(posSubjects)) {
                      allSubjects = allSubjects.concat(posSubjects)
                    }
                  })
                }
                if (subjectsConfig.common && Array.isArray(subjectsConfig.common)) {
                  allSubjects = allSubjects.concat(subjectsConfig.common)
                }
                
                if (allSubjects.length > 0) {
                  let latestEnd = null
                  allSubjects.forEach(subj => {
                    if (subj.date && subj.end_time) {
                      // 解析日期格式：2025年12月20日 -> 2025-12-20
                      const dateMatch = subj.date.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
                      const timeMatch = subj.end_time.match(/(\d{1,2}):(\d{2})/)
                      // 检查是否是下午
                      const isPM = subj.period === '下午'
                      if (dateMatch && timeMatch) {
                        let hour = parseInt(timeMatch[1])
                        // 如果是下午且小时数小于12，需要加12
                        if (isPM && hour < 12) {
                          hour += 12
                        }
                        const endDateTime = new Date(
                          parseInt(dateMatch[1]),
                          parseInt(dateMatch[2]) - 1,
                          parseInt(dateMatch[3]),
                          hour,
                          parseInt(timeMatch[2])
                        )
                        console.log('科目结束时间:', { subj: subj.name, endDateTime: endDateTime.toString() })
                        if (!isNaN(endDateTime.getTime()) && (!latestEnd || endDateTime > latestEnd)) {
                          latestEnd = endDateTime
                        }
                      }
                    }
                  })
                  if (latestEnd) {
                    console.log('最晚结束时间:', { latestEnd: latestEnd.toString(), now: now.toString(), isEnded: latestEnd < now })
                    if (latestEnd < now) {
                      isEnded = true
                    }
                  }
                }
              } catch (e) {
                console.warn('解析ticket_subjects失败', e)
              }
            }
            
            // 3. 如果没有从上面判断出结果，使用 end_time
            if (!isEnded && examEndTime) {
              const examEnd = new Date(examEndTime)
              if (!isNaN(examEnd.getTime()) && examEnd < now) {
                isEnded = true
              }
            }
            
            return {
              exam_id: app.exam_id,
              exam_title: app.exam_title || app.exam?.title || '-',
              exam_time: examTimeText || this.formatExamTime(examStartTime, examEndTime),
              audit_status: app.audit_status,
              score_status: isEnded ? '成绩待公布' : '考试未结束'
            }
          })
      } catch (e) {
        console.error('加载成绩失败', e)
        this.error = '成绩加载失败'
        this.scores = []
        this.pendingExams = []
      } finally {
        this.loading = false
      }
    },
    formatDate(d) {
      if (!d) return '-'
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return d
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const da = String(dt.getDate()).padStart(2, '0')
      const hh = String(dt.getHours()).padStart(2, '0')
      const mm = String(dt.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${da} ${hh}:${mm}`
    },
    formatExamTime(start, end) {
      const s = this.formatDate(start)
      const e = this.formatDate(end)
      if (s === '-' && e === '-') return '-'
      return `${s} - ${e}`
    },
    // 不在前端展示及格/不及格判断，避免用户直接看到合格状态
  },
  mounted() {
    this.fetchScores()
  }
}
</script>

<style scoped>
.score-query {
  padding: 20px;
}
.title {
  margin: 0 0 16px;
  font-size: 18px;
  color: var(--color-primary);
}
.section-title {
  margin: 20px 0 12px;
  font-size: 15px;
  color: #333;
  font-weight: 600;
}
.pending-section {
  margin-top: 30px;
}
.filters {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
}
.input {
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  min-width: 140px;
}
.btn {
  padding: 8px 16px;
  background: var(--color-primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.btn:hover {
  opacity: 0.9;
}
.hint {
  color: var(--color-text-secondary);
  padding: 20px 0;
  text-align: center;
}
.error {
  color: var(--color-danger);
  padding: 20px 0;
  text-align: center;
}

/* 表格样式优化 */
.score-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
  table-layout: fixed;
}
.score-table th,
.score-table td {
  border-bottom: 1px solid var(--color-border);
  padding: 12px 10px;
  text-align: center;
  vertical-align: middle;
  word-break: break-word;
}
.score-table th {
  background: #f5f9ff;
  color: var(--color-primary);
  font-weight: 600;
  white-space: nowrap;
}
.score-table tbody tr:hover {
  background-color: #fafafa;
}

/* 我的成绩表格列宽（4列：考试名称、考试时间、我的分数、各科成绩） */
.score-table th:nth-child(1),
.score-table td:nth-child(1) {
  width: 18%;
  text-align: left;
}
.score-table th:nth-child(2),
.score-table td:nth-child(2) {
  width: 28%;
  text-align: left;
}
.score-table th:nth-child(3),
.score-table td:nth-child(3) {
  width: 14%;
}
.score-table th:nth-child(4),
.score-table td:nth-child(4) {
  width: 40%;
}

.score {
  color: var(--color-focus, #1890ff);
  font-size: 16px;
}
.subject-scores {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.subject-item {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f5f9ff;
  color: #334155;
}

/* 待出成绩表格列宽（4列）- 与上方表格前两列对齐 */
.pending-table th:nth-child(1),
.pending-table td:nth-child(1) {
  width: 10%;
  text-align: left;
}
.pending-table th:nth-child(2),
.pending-table td:nth-child(2) {
  width: 22%;
  text-align: left;
}
.pending-table th:nth-child(3),
.pending-table td:nth-child(3) {
  width: 34%;
}
.pending-table th:nth-child(4),
.pending-table td:nth-child(4) {
  width: 34%;
}

/* 分页样式 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.pagination .pagination-total {
  color: var(--color-text-secondary);
  font-size: 14px;
}

.pagination .page-size-select {
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 14px;
}

.pagination .btn-page {
  padding: 6px 12px;
  background: #f5f5f5;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.pagination .btn-page:hover:not(:disabled) {
  background: #e9ecef;
}

.pagination .btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination .page-current {
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  background: #1677ff;
  color: #fff;
  border-radius: 4px;
  font-size: 13px;
}

.pagination .page-info {
  font-size: 13px;
  color: var(--color-text-secondary);
}
</style>
